import { 
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  AfterViewInit,
  Renderer2,
  ChangeDetectorRef,
  OnDestroy,
  ElementRef, 
  ViewEncapsulation,
  Output,
  EventEmitter} from '@angular/core';
import { BdGraph, BdGraphFactory } from 'src/lib/graph/graph.model';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TreeTheme } from '../tree/tree.component';
import { EngineService } from 'src/app/engine/engine.service';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { ShowObjects } from 'src/app/engine/models/show-objects.model';
import { StoryState } from 'src/app/story/state/story.state';
import { Store } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';

export enum TreeSearchDisplayMode {
  List,
  Tree
}

@Component({
  selector: 'app-tree-search',
  templateUrl: './tree-search.component.html',
  styleUrls: ['./tree-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class TreeSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  public get query() {
    return this._query;
  }
  public set query(value) {
    this._query = value || '';
    this.handleQueryChange();
  }
  private _query = '';

  @Input()
  public get displayMode() {
    return this._displayMode;
  }
  public set displayMode(value) {
    if (value == null) {
      return;
    }

    this._displayMode = value;
    this.handleQueryChange();

    this.displayModeChange.emit(this.displayMode);
  }
  _displayMode = TreeSearchDisplayMode.List;
  
  @Output()
  displayModeChange = new EventEmitter<TreeSearchDisplayMode>();

  @Input()
  public get viewGraph(): BdGraph<ViewTreeNode> {
    return this._viewGraph;
  }
  public set viewGraph(value: BdGraph<ViewTreeNode>) {
    this._viewGraph = value;
    this.leafNodes = this.engineService.getLeaves();

    // create a pruned version of the viewGraph for interested consumers.
    this.buildPrunedViewGraph(this.displayMode);
    
    this.handleQueryChange();
    this.changeDetectorRef.markForCheck();
  }
  private _viewGraph: BdGraph<ViewTreeNode>;

  @Input()
  theme: TreeTheme = TreeTheme.Dark;

  treeSearchDisplayMode = TreeSearchDisplayMode;
  searchResults: ViewTreeNode[] = [];
  searchHeight = 0;
  ngUnsubscribe = new Subject();
  leafNodes: string[];
  searchGraph: BdGraph<ViewTreeNode>;
  prunedViewGraph: BdGraph<ViewTreeNode>;

  constructor(
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    private engineService: EngineService,
    private graphService: BdGraphService,
    private store: Store
  ) { }

  ngOnInit() {
  }

  buildPrunedViewGraph(mode: TreeSearchDisplayMode) {
  
    // prune the graph according to chapter show objects
    const currentChapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);

    const chapterShowObjects: ShowObjects = this.store
      .selectSnapshot(StoryState.getStory)
      .chapters.find(c => c.id === currentChapterId)
      .showObjects;

    const prunedViewGraph: BdGraph<ViewTreeNode> = this.engineService.createViewGraph(
      chapterShowObjects,
      true // always prune on view page
    );

    this.prunedViewGraph = prunedViewGraph;
  }

  ngAfterViewInit() {
    // Window has resized, let the 3D canvas know
    this.renderer.listen('window', 'resize', (event) => {
      this.resize();
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleQueryChange() {
    const searchResults = this.search(
      this._query,
      this.displayMode === TreeSearchDisplayMode.List
        ? this.prunedViewGraph
        : this.viewGraph
    );

    if (this.displayMode === TreeSearchDisplayMode.Tree) {
      this.searchGraph = this.getTree(searchResults);
    }

    this.searchResults = searchResults;
    this.resize();
  }

  // starting with the leaf nodes matching a query, build a viw graph from the bottom up
  getTree(searchResults: ViewTreeNode[]): BdGraph<ViewTreeNode> {
    const newGraph = BdGraphFactory<ViewTreeNode>();
    searchResults.forEach(node => {
      this.graphService.addNode(newGraph, node);
    });
    
    // work up the ancestor list for each node
    searchResults.forEach(node => {
      const ancestors = this.graphService.getAncestors(this.viewGraph, node.id, false);
      let prevnode: ViewTreeNode = node;
      
      ancestors.every( ancestor => {
        const addnodes: ViewTreeNode[] = this.graphService.getNodes(newGraph, [ancestor]);
        let addnode: ViewTreeNode = null;
        
        if (addnodes.length === 0) {
          addnode = this.graphService.getNodes(this.viewGraph, [ancestor])[0];
          
          if (addnode.depth === 0) {
            this.graphService.addRootNode(newGraph, addnode);
          } else {
            this.graphService.addNode(newGraph, addnode);
          }

        } else {
          addnode = addnodes[0];
        }
        
        // build edges up the tree, skip to the next leaf node if we hit an existing edge
        const keepClimbing = this.graphService.addEdge(newGraph, addnode.id, prevnode.id);
        prevnode = addnode;

        return keepClimbing;
      });
    });

    return newGraph;
  }

  resize() {
    this.getSearchHeight()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((height) => {
        this.searchHeight = height;
        this.changeDetectorRef.markForCheck();
      });
  }

  getSearchHeight(): Observable<number> {
    const e = this.elementRef;

    return new Observable<number>((observer) => {
      setTimeout(() => {
        const offsetHeight = this.elementRef.nativeElement.offsetHeight;
  
        if (offsetHeight != null) {
          observer.next(offsetHeight);
          observer.complete();
        }
      });
    });
  }

  // return a sorted array of leaf nodes matching a query
  search(query: string, viewGraph: BdGraph<ViewTreeNode>): ViewTreeNode[] {
    if (viewGraph === undefined) {
      return [];
    }

    query = query.toLowerCase();
    const results = Object.values(viewGraph.nodes)
      .filter((node: ViewTreeNode) => this.leafNodes.indexOf(node.id) >= 0)
      .filter((node: ViewTreeNode) => query.length === 0 ? true : node.label.toLowerCase().indexOf(query) >= 0 )
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
    return results;
  }

  trackTreeNode(index: number, node: ViewTreeNode) { 
    return node.id; 
  }
  
}
